// src/store/thunks/sixDetail/fetchMetaAndFilesThunk.ts
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import {
  FILE_LIST_TOPIC,
  FILE_TRANSFER_TOPIC,
} from '@/config/networkConstants';
import {
  mapParticipantsPayloadDTOToParticipantsResponse,
  mapSixFilesApiResponseToFileEntries,
  mapSixSummaryDTOToSixDetailMeta,
} from '@/store/mappers/mapSixDetail';
import { initializeP2PStackThunk } from '@/store/thunks/poc/connection/initializeP2PStackThunk';
import { syncFilesFromApiThunk } from '@/store/thunks/sixDetail/syncFilesFromApiThunk';
import type { FetchFilesParams } from '@/types/SixDetailDTOs';
import type { SixDetailState } from '@/types/SixDetail.types';

function buildFetchFilesParams(state: SixDetailState): FetchFilesParams {
  const params: FetchFilesParams = {
    filters: state.activeFilters,
    search: state.search,
    sortKey: state.sortKey,
    sortDir: state.sortDir,
  };

  return params;
}

export const fetchSixDetailMetaAndFilesThunk = createAppAsyncThunk(
  'sixDetail/fetchMetaAndFiles',
  async(sixIdentifier: string, { dispatch, extra, getState }) => {
    const rootState = getState();
    const { activeFilters } = rootState.sixDetail;

    const metaDTO = await extra.sixDetailService.getMeta(sixIdentifier, activeFilters);
    const bootstrapNodes = metaDTO.bootstrapNodes
      .map((bootstrapNode) => bootstrapNode.trim())
      .filter((bootstrapNode) => bootstrapNode.length > 0);

    if (bootstrapNodes.length > 0) {
      void dispatch(
        initializeP2PStackThunk({
          sixId: sixIdentifier,
          bootstrapNodes,
          swarmKey: metaDTO.swarmKey,
          fileListTopic: FILE_LIST_TOPIC(sixIdentifier),
          fileTransferTopic: FILE_TRANSFER_TOPIC,
          autoInitializeMessaging: true,
        })
      );
    }

    const filesResponse = await extra.sixDetailService.fetchFiles(
      sixIdentifier,
      buildFetchFilesParams(rootState.sixDetail)
    );
    const participantsPayload = await extra.sixDetailService.fetchParticipants(sixIdentifier);

    const meta = mapSixSummaryDTOToSixDetailMeta(metaDTO);
    const participants = mapParticipantsPayloadDTOToParticipantsResponse(participantsPayload);
    const files = mapSixFilesApiResponseToFileEntries(filesResponse);
    if (typeof filesResponse.totalFileCount === 'number' && meta.fileCount === undefined) {
      meta.fileCount = filesResponse.totalFileCount;
    }

    void dispatch(syncFilesFromApiThunk({ sixId: sixIdentifier, files }));

    return { meta, files, participants };
  },
);
